/* global theme, trapFocus */

/**
 * Quick add for products in the cart drawer: the upsell offers
 * (snippets/cart-upsell.liquid) and "You may like" recommendations, using the
 * controls from snippets/cart-quick-add.liquid.
 * - Add on a product without sizes adds it to the cart straight away.
 * - Add on a product with sizes opens a size picker; picking a size adds it.
 * - The size picker's size guide link opens the size guide page's content in
 *   the theme's modal, over the drawer.
 * When the element has data-upsell-label (the upsell), added lines carry it as
 * a hidden _upsell line item property. On success, on:cart:change makes the
 * cart drawer and header re-render.
 */
class CartQuickAdd extends HTMLElement {
  connectedCallback() {
    this.boundHandleClick = this.handleClick.bind(this);
    this.boundHandleKeyup = this.handleKeyup.bind(this);
    this.addEventListener('click', this.boundHandleClick);
    this.addEventListener('keyup', this.boundHandleKeyup);

    // The upsell's carousel measures itself, so refresh it once the drawer is
    // visible ("You may like" is refreshed by its own cc-cart-cross-sell).
    if (this.hasAttribute('data-refresh-carousel')) {
      this.drawer = this.closest('cart-drawer');
      this.boundRefreshCarousel = this.refreshCarousel.bind(this);
      if (this.drawer) {
        this.drawer.addEventListener('on:cart-drawer:before-open', this.boundRefreshCarousel);
        if (this.drawer.hasAttribute('open')) this.refreshCarousel();
      }
    }
  }

  disconnectedCallback() {
    this.removeEventListener('click', this.boundHandleClick);
    this.removeEventListener('keyup', this.boundHandleKeyup);
    if (this.drawer) {
      this.drawer.removeEventListener('on:cart-drawer:before-open', this.boundRefreshCarousel);
    }
  }

  refreshCarousel() {
    const carousel = this.querySelector('carousel-slider');
    if (carousel && typeof carousel.refresh === 'function') {
      requestAnimationFrame(() => carousel.refresh());
    }
  }

  /**
   * @param {object} evt - Click event.
   */
  handleClick(evt) {
    if (this.adding) return;

    const openBtn = evt.target.closest('[data-open-sizes]');
    if (openBtn) {
      evt.preventDefault();
      this.openSizes(openBtn);
      return;
    }

    const sizeGuideLink = evt.target.closest('[data-size-guide]');
    if (sizeGuideLink) {
      evt.preventDefault();
      this.openSizeGuide(sizeGuideLink);
      return;
    }

    const closeBtn = evt.target.closest('[data-close-sizes]');
    if (closeBtn) {
      evt.preventDefault();
      this.closeSizes(closeBtn.closest('.cart-quick-add__sizes'));
      return;
    }

    const variantBtn = evt.target.closest('[data-variant-id]');
    if (variantBtn) {
      evt.preventDefault();
      this.addToCart(variantBtn);
    }
  }

  /**
   * Close an open size picker on Escape (the drawer ignores Escape from
   * inside .cart-drawer-popup, so it stays open).
   * @param {object} evt - Keyup event.
   */
  handleKeyup(evt) {
    if (evt.key !== 'Escape') return;
    const panel = evt.target.closest('.cart-quick-add__sizes');
    if (panel) this.closeSizes(panel);
  }

  /**
   * Show the size picker for a product.
   * @param {Element} openBtn - The product's Add button.
   */
  openSizes(openBtn) {
    this.querySelectorAll('.cart-quick-add__sizes:not([hidden])').forEach((panel) => this.closeSizes(panel, false));

    const panel = this.querySelector(`#${CSS.escape(openBtn.getAttribute('aria-controls'))}`);
    if (!panel) return;
    panel.hidden = false;
    openBtn.setAttribute('aria-expanded', 'true');
    const item = openBtn.closest('[data-quick-add-item]');
    if (item) item.classList.add('is-choosing-size');
    const firstOption = panel.querySelector('[data-variant-id]');
    if (firstOption) firstOption.focus();
  }

  /**
   * Hide a size picker.
   * @param {Element} panel - The size picker.
   * @param {boolean} [returnFocus=true] - Move focus back to the product's Add button.
   */
  closeSizes(panel, returnFocus = true) {
    if (!panel) return;
    panel.hidden = true;
    const item = panel.closest('[data-quick-add-item]');
    if (item) item.classList.remove('is-choosing-size');
    const openBtn = this.querySelector(`[aria-controls="${panel.id}"]`);
    if (openBtn) {
      openBtn.setAttribute('aria-expanded', 'false');
      if (returnFocus) openBtn.focus();
    }
  }

  /**
   * Open a size guide page in a modal. Falls back to going to the page if its
   * content can't be loaded.
   * @param {Element} link - Size guide link.
   */
  async openSizeGuide(link) {
    if (link.classList.contains('is-loading')) return;
    link.classList.add('is-loading');

    let content;
    try {
      content = await CartQuickAdd.fetchSizeGuide(link.href);
    } catch (error) {
      window.location.href = link.href;
      return;
    } finally {
      link.classList.remove('is-loading');
    }

    const modal = this.getSizeGuideModal();
    modal.setAttribute('aria-label', link.dataset.sizeGuideTitle || link.textContent.trim());
    modal.querySelector('.modal__content').innerHTML = content;
    modal.open(link);

    // The modal takes over the drawer's focus trap; give it back on close.
    const drawer = this.closest('cart-drawer');
    const observer = new MutationObserver(() => {
      if (modal.hasAttribute('open')) return;
      observer.disconnect();
      if (drawer && drawer.hasAttribute('open') && typeof trapFocus === 'function') {
        trapFocus(drawer, link.isConnected ? link : drawer);
      }
    });
    observer.observe(modal, { attributes: true, attributeFilter: ['open'] });
  }

  /**
   * Get (creating on first use) the size guide modal. It lives on the body, as
   * the drawer's transform would otherwise contain it.
   * @returns {Element}
   */
  getSizeGuideModal() {
    let modal = document.getElementById('CartSizeGuideModal');
    if (modal) return modal;

    const closeBtn = this.querySelector('[data-close-sizes]');
    const closeLabel = closeBtn ? closeBtn.getAttribute('aria-label') : 'Close';
    const closeIcon = closeBtn && closeBtn.querySelector('svg') ? closeBtn.querySelector('svg').outerHTML : '&times;';
    const wrapper = document.createElement('div');
    wrapper.innerHTML = `
      <modal-dialog class="modal modal--for-product cart-size-guide fixed top-0 left-0 w-full h-full flex items-center justify-center" id="CartSizeGuideModal" role="dialog" aria-modal="true" tabindex="-1">
        <div class="modal__window relative bg-theme-bg text-theme-text text-start overflow-hidden has-motion">
          <button type="button" class="modal__close-btn absolute js-close-modal" aria-label="${closeLabel.replace(/"/g, '&quot;')}">${closeIcon}</button>
          <div class="modal__content flex-auto h-full rte"></div>
        </div>
      </modal-dialog>`;
    modal = wrapper.firstElementChild;
    document.body.appendChild(modal);
    return modal;
  }

  /**
   * Fetch a size guide page's content (cached per page).
   * @param {string} url - Page URL.
   * @returns {Promise<string>} The page content's HTML.
   */
  static fetchSizeGuide(url) {
    CartQuickAdd.sizeGuides = CartQuickAdd.sizeGuides || new Map();
    if (!CartQuickAdd.sizeGuides.has(url)) {
      const request = new URL(url, window.location.origin);
      request.searchParams.set('section_id', 'main-page');
      const promise = fetch(request.toString())
        .then((response) => {
          if (!response.ok) throw new Error(response.statusText);
          return response.text();
        })
        .then((html) => {
          const content = new DOMParser().parseFromString(html, 'text/html').querySelector('.rte');
          if (!content) throw new Error('No size guide content');
          return content.innerHTML;
        });
      promise.catch(() => CartQuickAdd.sizeGuides.delete(url));
      CartQuickAdd.sizeGuides.set(url, promise);
    }
    return CartQuickAdd.sizeGuides.get(url);
  }

  /**
   * Add a variant to the cart.
   * @param {Element} button - Button with a data-variant-id.
   */
  async addToCart(button) {
    const item = button.closest('[data-quick-add-item]');
    const variantId = Number(button.dataset.variantId);
    const upsellLabel = this.dataset.upsellLabel;
    const line = { id: variantId, quantity: 1 };
    if (upsellLabel) line.properties = { _upsell: upsellLabel };

    this.adding = true;
    this.setError(item, false);
    this.querySelectorAll('[data-variant-id], [data-open-sizes]').forEach((el) => el.setAttribute('aria-disabled', 'true'));
    button.classList.add('is-loading');

    try {
      const response = await fetch(theme.routes.cartAdd, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json',
          'X-Requested-With': 'XMLHttpRequest'
        },
        body: JSON.stringify({ items: [line] })
      });
      const data = await response.json();
      if (!response.ok) {
        throw new Error(data.description || data.message || this.dataset.errorText);
      }

      this.closeSizes(button.closest('.cart-quick-add__sizes'), false);
      this.dispatchEvent(new CustomEvent('on:cart:add', {
        bubbles: true,
        detail: { variantId, upsell: upsellLabel }
      }));
      // The drawer re-renders on this event.
      document.dispatchEvent(new CustomEvent('on:cart:change', { bubbles: true, cancelable: false }));
    } catch (error) {
      this.closeSizes(button.closest('.cart-quick-add__sizes'));
      this.setError(item, error.message || this.dataset.errorText);
    }

    this.querySelectorAll('[aria-disabled="true"]').forEach((el) => el.removeAttribute('aria-disabled'));
    button.classList.remove('is-loading');
    this.adding = false;
  }

  /**
   * Show or hide a product's error message.
   * @param {Element} item - The product's container.
   * @param {string|boolean} message - Message to show, or false to hide.
   */
  setError(item, message) {
    const errorEl = item && item.querySelector('.cart-quick-add__error');
    if (!errorEl) return;
    errorEl.textContent = message || '';
    errorEl.hidden = !message;
  }
}

if (!customElements.get('cart-quick-add')) {
  customElements.define('cart-quick-add', CartQuickAdd);
}
